'use client';

import { EventsCarouselSkeleton } from '@v2/components/EventsCarouselSkeleton/EventsCarouselSkeleton';
import { EventCardDTO } from '@v2/types';
import { useIsConcerts } from '@v2/views/events/use-is-concerts';
import { useEffect, useRef, useState } from 'react';
import { LeagueCarouselEvents } from '../components/LeagueCarouselEvents/LeagueCarouselEvents';
import { getPopularLeagueEvents } from './popularLeagueEvents';

type Props = {
  title: string;
  leagueName: string;
  leagueNames?: string[];
  perPage?: number;
  maxEvents?: number;
  isConcerts?: boolean;
  concertTitle?: string;
  initialEvents?: EventCardDTO[];
};

export const LeagueCarouselEventsWrapper = ({
  title,
  leagueName,
  leagueNames,
  perPage,
  maxEvents,
  isConcerts,
  concertTitle,
  initialEvents,
}: Props) => {
  const queryIsConcerts = useIsConcerts();
  const shouldUseConcerts = isConcerts ?? queryIsConcerts;
  const hasSeededInitialEvents = Array.isArray(initialEvents);
  const [events, setEvents] = useState<EventCardDTO[] | undefined>(
    hasSeededInitialEvents ? initialEvents : undefined
  );
  const categoryNamesKey = (
    leagueNames?.length ? leagueNames : [leagueName]
  ).join('|');
  const shouldUseInitialEvents = useRef(hasSeededInitialEvents);

  useEffect(() => {
    if (shouldUseInitialEvents.current) {
      shouldUseInitialEvents.current = false;
      return;
    }

    let isActive = true;

    getPopularLeagueEvents({
      categoryNames: categoryNamesKey.split('|'),
      concerts: shouldUseConcerts,
      perPage,
      maxEvents,
    })
      .then(async (resolvedEvents) => {
        const { mapEventCardDTOs } = await import('../util/mapEventCardDTO');
        if (isActive) setEvents(mapEventCardDTOs(resolvedEvents));
      })
      .catch(() => {
        if (isActive) setEvents([]);
      });

    return () => {
      isActive = false;
    };
  }, [categoryNamesKey, maxEvents, perPage, shouldUseConcerts]);

  if (!events) return <EventsCarouselSkeleton />;

  const resolvedTitle =
    queryIsConcerts && concertTitle
      ? `Popular ${concertTitle} Concerts`
      : title;

  return (
    <LeagueCarouselEvents
      title={resolvedTitle}
      events={events}
      itemListName="league_popular_events"
      itemCategory={leagueName}
    />
  );
};
